import { useState } from "react";

const FONT_STACK =
  "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, sans-serif";

/* ------------------------------------------------------------------ shared */

/* ------------------------------------------------------------------ hero */

function Hero() {
  return (
    <div className="relative flex min-h-[70px] max-h-[90px] flex-wrap items-center justify-between gap-3 overflow-hidden rounded-[16px] bg-gradient-to-br from-[#c9f5dd] via-[#9fe9c4] to-[#74dca6] px-6">
      <div className="flex flex-col gap-0.5">
        <div className="text-[10px] font-extrabold tracking-[0.09em] text-[#0a7a4f]">TUESDAY · JUNE 4</div>
        <h1 className="m-0 whitespace-nowrap text-[22px] font-extrabold leading-tight tracking-[-0.03em] text-[#06281b]">
          Good evening, Alex.
        </h1>
      </div>
      <div className="flex gap-2">
        {([ ["0", "Torneos activos"], ["0", "Próximos eventos"], ["0", "Grupos sin torneo"], ["0", "Torneos por cerrar"] ] as [string, string][]).map(([n, l]) => (
          <div key={l} className="rounded-xl bg-white/70 px-3 py-2 backdrop-blur-sm">
            <div className="text-[18px] font-extrabold leading-none tracking-[-0.02em] text-[#06281b]">{n}</div>
            <div className="mt-0.5 text-[9px] font-extrabold uppercase tracking-[0.06em] text-[#0a7a4f]">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* --------------------------------------------------- sport switcher tabs */

const STATUS_TABS = ["Todos", "Próximos", "En curso", "Finalizados", "Pendientes", "Sin ganador", "Sin torneo asignado"] as const;
type StatusTab = typeof STATUS_TABS[number];

function StatusTabs({ active, onChange }: { active: StatusTab; onChange: (v: StatusTab) => void }) {
  return (
    <div className="flex gap-1 overflow-x-auto rounded-[14px] bg-[#0b2e25] p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {STATUS_TABS.map((tab) => (
        <button
          key={tab}
          onClick={() => onChange(tab)}
          className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-[10px] px-3.5 py-[9px] text-[13px] font-extrabold transition ${
            active === tab
              ? "bg-emerald-500 text-[#04231b]"
              : "text-[#7fd4b8] hover:text-[#a7e6cf]"
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}


interface TournamentCardData {
  id: string;
  nombre: string;
  grupo: string;
  estado: string;
  lider: string;
  proximoEvento: string;
  progreso: [number, number];
}

const SAMPLE_TOURNAMENTS: TournamentCardData[] = [
  { id: "t1", nombre: "Liga BetPlay Apertura", grupo: "Elite Pro Tips", estado: "En curso", lider: "Juan Pérez", proximoEvento: "Fecha 6 · Mañana 18:30", progreso: [5, 10] },
  { id: "t2", nombre: "Copa Sudamericana", grupo: "Weekend Acca #42", estado: "Próximo", lider: "María López", proximoEvento: "Fecha 1 · Sábado 15:00", progreso: [0, 8] },
  { id: "t3", nombre: "Liga Águila", grupo: "Champions Talk", estado: "Pendiente", lider: "Carlos Ruiz", proximoEvento: "Fecha 3 · Viernes 20:00", progreso: [2, 6] },
  { id: "t4", nombre: "Torneo Clausura", grupo: "Elite Pro Tips", estado: "Finalizado", lider: "Ana Gómez", proximoEvento: "—", progreso: [10, 10] },
];

const ESTADO_COLORS: Record<string, string> = {
  "En curso":   "bg-white/90 text-[#0f9d58]",
  "Próximo":    "bg-white/90 text-[#1a73e8]",
  "Pendiente":  "bg-white/90 text-[#f29900]",
  "Finalizado": "bg-white/20 text-white",
};

function TournamentCard({ t }: { t: TournamentCardData }) {
  const [completados, total] = t.progreso;
  const pct = total > 0 ? Math.round((completados / total) * 100) : 0;
  return (
    <div className="relative flex flex-col gap-2.5 overflow-hidden rounded-[16px] bg-gradient-to-b from-[#1faa75] to-[#0d8256] px-4 pb-4 pt-3 text-white shadow-[0_8px_20px_rgba(13,130,86,0.22)]">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#74dca6] to-[#0f9d58]" />

      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[13.5px] font-extrabold tracking-[-0.01em] text-white">{t.nombre}</div>
          <div className="text-[11px] text-[#c4f0da]">Grupo: {t.grupo}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-[2px] text-[10px] font-extrabold ${ESTADO_COLORS[t.estado] ?? "bg-white/20 text-white"}`}>
          {t.estado}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-y-1 text-[11.5px]">
        <span className="text-[#d3f4e3]">Líder</span>
        <span className="font-semibold text-white">{t.lider}</span>
        <span className="text-[#d3f4e3]">Próximo evento</span>
        <span className="font-semibold text-white">{t.proximoEvento}</span>
      </div>

      <div>
        <div className="mb-1 flex justify-between text-[10px] font-bold uppercase tracking-[0.06em] text-[#d3f4e3]">
          <span>Progreso</span>
          <span>{completados}/{total} eventos completados</span>
        </div>
        <div className="flex h-[5px] overflow-hidden rounded-full bg-white/[0.26]">
          <div className="rounded-full bg-gradient-to-r from-white to-[#cdf7e2]" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <button className="w-full rounded-lg bg-white/[0.18] py-2 text-[12px] font-bold text-white transition hover:bg-white/[0.28]">
        Administrar torneo
      </button>
    </div>
  );
}

/* ---------------------------------------------------------- tournaments section */

function TournamentsSection() {
  const [statusTab, setStatusTab] = useState<StatusTab>("Todos");
  return (
    <section>
      <div className="mb-2">
        <h2 className="m-0 text-[20px] font-extrabold tracking-[-0.02em] text-[#0b1220]">
          Torneos y eventos activos
        </h2>
      </div>

      <div className="mb-2">
        <StatusTabs active={statusTab} onChange={setStatusTab} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {SAMPLE_TOURNAMENTS.map((t) => (
          <TournamentCard key={t.id} t={t} />
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ your groups */

interface GroupCardData {
  id: string;
  nombre: string;
  miembros: number;
  torneoActivo: string;
  estado: string;
  eventosPendientes: number;
  ultimoGanador: string;
  accion: string;
}

const SAMPLE_GROUPS: GroupCardData[] = [
  { id: "g1", nombre: "Elite Pro Tips", miembros: 124, torneoActivo: "Liga BetPlay Apertura", estado: "Activo", eventosPendientes: 3, ultimoGanador: "Juan Pérez", accion: "Ver grupo" },
  { id: "g2", nombre: "Weekend Acca #42", miembros: 48, torneoActivo: "Sin torneo asignado", estado: "Sin torneo", eventosPendientes: 0, ultimoGanador: "Copa Sudamericana 2024", accion: "Asignar torneo" },
  { id: "g3", nombre: "Champions Talk", miembros: 312, torneoActivo: "Liga Águila", estado: "Pendiente", eventosPendientes: 5, ultimoGanador: "María López", accion: "Revisar pendientes" },
];

const GRUPO_ESTADO_COLORS: Record<string, string> = {
  "Activo":       "bg-[#e8f7ef] text-[#0f9d58]",
  "Sin torneo":   "bg-[#fef3e2] text-[#f29900]",
  "Pendiente":    "bg-[#fef3e2] text-[#f29900]",
  "Finalizado":   "bg-[#f1f3f4] text-[#5f6368]",
};

function GroupCard({ g }: { g: GroupCardData }) {
  return (
    <div className="flex flex-col gap-2 rounded-[16px] border border-[#eef0f2] bg-white px-4 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] font-extrabold text-[#0b1220]">{g.nombre}</div>
        <span className={`shrink-0 rounded-full px-2 py-[2px] text-[10px] font-extrabold ${GRUPO_ESTADO_COLORS[g.estado] ?? "bg-[#f1f3f4] text-[#5f6368]"}`}>
          {g.estado}
        </span>
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
        <span className="text-[#94a3b8]">Miembros</span>
        <span className="font-semibold text-[#0b1220]">{g.miembros}</span>
        <span className="text-[#94a3b8]">Torneo activo</span>
        <span className="font-semibold text-[#0b1220] truncate">{g.torneoActivo}</span>
        <span className="text-[#94a3b8]">Eventos pendientes</span>
        <span className="font-semibold text-[#0b1220]">{g.eventosPendientes}</span>
        <span className="text-[#94a3b8]">Último ganador</span>
        <span className="font-semibold text-[#0b1220] truncate">{g.ultimoGanador}</span>
      </div>

      <button className="w-full rounded-lg border border-[#e5e7eb] bg-white py-1.5 text-[11.5px] font-bold text-[#0f9d58] transition hover:bg-[#f0fdf4]">
        {g.accion}
      </button>
    </div>
  );
}

function GroupsSection() {
  return (
    <section className="mt-4">
      <div className="mb-3 flex items-end justify-between">
        <div>
          <h2 className="m-0 text-[18px] font-extrabold tracking-[-0.02em] text-[#0b1220]">
            Grupos de apuestas
          </h2>
        </div>
        <button className="text-[12px] font-bold text-[#0f9d58]">
          Ver todos
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {SAMPLE_GROUPS.map((g) => <GroupCard key={g.id} g={g} />)}
      </div>
    </section>
  );
}

/* -------------------------------------------------------- your predictions */

const ACCIONES = [
  { id: "a1", texto: "3 torneos sin ganador registrado" },
  { id: "a2", texto: "6 grupos sin torneo activo" },
  { id: "a3", texto: "4 eventos pendientes por revisar" },
  { id: "a4", texto: "2 torneos finalizados sin cierre" },
  { id: "a5", texto: "5 eventos próximos hoy" },
];

function PendingActionsPanel() {
  return (
    <div className="self-start rounded-[16px] border border-[#eef0f2] bg-white px-4 py-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="whitespace-nowrap text-[13.5px] font-extrabold tracking-[-0.01em] text-[#0b1220]">
          Acciones pendientes
        </div>
        <span className="flex-shrink-0 whitespace-nowrap rounded-full bg-[#e8f7ef] px-2 py-[2px] text-[10px] font-extrabold text-[#0f9d58]">
          {ACCIONES.length}
        </span>
      </div>
      <div className="mt-0.5 text-[11px] font-semibold text-[#94a3b8]">
        Elementos que requieren tu atención
      </div>
      <div className="mt-2 flex flex-col">
        {ACCIONES.map((a, i) => (
          <div key={a.id} className={`flex items-center gap-2.5 py-2.5 ${i < ACCIONES.length - 1 ? "border-b border-[#f1f5f9]" : ""}`}>
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#f29900]" />
            <span className="text-[12px] font-semibold text-[#0b1220]">{a.texto}</span>
          </div>
        ))}
      </div>
      <button className="mt-3 w-full rounded-lg border border-[#e5e7eb] bg-white py-2 text-[12px] font-bold text-[#0f9d58] transition hover:bg-[#f0fdf4]">
        Requieren atención
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ shell */

export function Dashboard() {
  return (
    <div
      className="min-h-screen bg-[#f3f4f6] px-5 pb-8 pt-2 text-[#0b1220]"
      style={{ fontFamily: FONT_STACK }}
    >
      <div className="mx-auto max-w-[1320px]">
        <Hero />

        <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            <TournamentsSection />
            <GroupsSection />
          </div>
          <PendingActionsPanel />
        </div>
      </div>
    </div>
  );
}
